import { Button, DotsHorizontalIcon, DotsVerticalIcon, Dropdown, Select, SelectOptionType } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { CSSProperties, FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
// Raumzuweisung beim Ablegen im Raumplan vorerst deaktiviert – Import bleibt bei Reaktivierung nötig.
// import { RoomItem } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { dayToTranslation } from '../../../utils/dayToTranslation';
import { getSubjectCardTextColor } from '../../../utils/getCardTextColor';
import { Slider } from '../../Slider/Slider';
import { useTimetableStore } from '../TimetableProvider';
import styles from './TimetablePreview.module.scss';

export type ResourceTimetableKind = 'class' | 'teacher' | 'room';

/**
 * Farbsegment einer (Teil-)Karte in einer Zelle. Lehrkraft/Raum füllen den ganzen Slot; im
 * Klassenplan werden die Teilungen NEBENEINANDER gelegt (gleich breite Spalten, sortiert nach
 * ihrer vertikalen Board-Position).
 */
type Segment = {
  key: string;
  left: string;
  width: string;
  top: string;
  height: string;
  color?: string;
  textColor?: string;
  label?: string;
};

/** `marked`: die Ressource ist der Karte tatsächlich zugewiesen (Raumplan: der echte Raum). */
export type TimetableResource = { uuid: string; name: string; marked?: boolean };

export type ResourceTimetableProps = {
  kind: ResourceTimetableKind;
  title: string;
  resources: TimetableResource[];
  hours: { value: string; label: string }[];
  /** Tab, der beim Aufnehmen/Kartenwechsel vorausgewählt wird (Raumplan: der zugewiesene Raum). */
  defaultActiveUuid?: string;
  /** UUID der Zielkarte (in der Hand oder unter dem Cursor) → Hervorhebung ihrer eigenen Slots. */
  cardUuid: string;
  duration: number;
  /**
   * Karte in der Hand → Plan ist Ablageziel (Klick steckt, grüne/rote Zellen). Beim reinen Hover
   * ist der Plan nur informativ: keine Klick-Ablage, kein Drop-Feedback.
   */
  interactive: boolean;
};

/**
 * Ein klassischer Wochen-Stundenplan (Spalten = Tage, Zeilen = Stunden) einer einzelnen Ressource
 * (Lehrkraft oder Raum). Bei mehreren Ressourcen wechselt eine Tab-Leiste zwischen ihnen. Jede
 * Zelle ist Ablageziel: ein Klick steckt die Karte in der Hand auf Tag/Stunde – im Raumplan wird
 * zusätzlich der Raum des aktiven Tabs zugewiesen (siehe `placeCardFromPreview`).
 */
export const ResourceTimetable: FC<ResourceTimetableProps> = observer(
  ({ kind, title, resources, hours, defaultActiveUuid, cardUuid, duration, interactive }) => {
    const store = useTimetableStore();
    const { t } = useTranslation();
    const colorBySubject = store.cardColorBySubject;

    // Vorauswahl: der zugewiesene Raum (defaultActiveUuid), sonst die erste Ressource.
    const [activeUuid, setActiveUuid] = useState<string>(
      () => resources.find((r) => r.uuid === defaultActiveUuid)?.uuid ?? resources[0]?.uuid ?? '',
    );
    const lastCardRef = useRef(cardUuid);

    // Bei Kartenwechsel den zugewiesenen Raum vorwählen (manuelle Tab-Wahl gilt nur für dieselbe
    // Karte). Wird der aktive Tab ungültig (Ressourcen-Wechsel), ebenfalls auf die Vorauswahl fallen.
    useEffect(() => {
      const preferred = resources.find((r) => r.uuid === defaultActiveUuid)?.uuid ?? resources[0]?.uuid ?? '';
      if (lastCardRef.current !== cardUuid) {
        lastCardRef.current = cardUuid;
        setActiveUuid(preferred);
      } else if (!resources.some((r) => r.uuid === activeUuid)) {
        setActiveUuid(preferred);
      }
    }, [cardUuid, resources, activeUuid, defaultActiveUuid]);

    if (resources.length === 0) {
      return (
        <div className={styles.section}>
          <div className={styles['section-title']}>{title}</div>
          <div className={styles.empty}>{t('pinboard.timetablePreview.empty')}</div>
        </div>
      );
    }

    const handleCellClick = (dayValue: string, hour: { value: string; label: string }) => {
      if (!interactive) return;
      const dayItem = store.days.find((d) => d.value === dayValue);
      if (!dayItem) return;
      // Raumzuweisung beim Ablegen im Raumplan vorerst deaktiviert (kann wiederkommen):
      // const room: RoomItem | undefined =
      //   kind === 'room'
      //     ? { value: activeUuid, label: store.rooms.get(activeUuid)?.name ?? '', group: '', inUse: false }
      //     : undefined;
      store.placeCardFromPreview(dayItem, { value: hour.value, label: hour.label, isPause: false });
    };

    // Optionen für den Select. Im Raumplan werden die zugewiesenen Räume vorangestellt und mit
    // Gruppen-Überschriften versehen; Lehrer/Klassen bleiben eine flache, alphabetische Liste.
    const isRoom = kind === 'room';
    const orderedResources = isRoom
      ? [...resources].sort((a, b) => Number(Boolean(b.marked)) - Number(Boolean(a.marked)))
      : resources;
    const selectOptions: SelectOptionType[] = orderedResources.map((resource) => ({
      value: resource.uuid,
      label: resource.name,
      group: isRoom
        ? resource.marked
          ? t('pinboard.timetablePreview.assigned')
          : t('pinboard.timetablePreview.roomSupply')
        : undefined,
    }));

    return (
      <div className={styles.section}>
        <div className={styles['section-header']}>
          <div className={styles['section-title']}>{title}</div>
          <div className={styles['header-actions']}>
            <Select
              readonly={selectOptions.length === 1}
              className={styles['resource-select']}
              dense
              isClearable={false}
              isSearchable={true}
              hasGroups={kind === 'room'}
              menuPosition='fixed'
              name={`tt-preview-${kind}`}
              value={selectOptions.find((option) => option.value === activeUuid) ?? null}
              options={selectOptions}
              onChange={(option) => {
                const opt = option as SingleValue<SelectOptionType>;
                if (opt) setActiveUuid(opt.value as string);
              }}
            />

            <Dropdown
              // Im Vollbild nicht in den body portalen – sonst liegt das Dropdown hinter dem
              // Vollbild-Element (#timetable-grid) und ist unsichtbar (siehe GridMenuBar).
              usePortal={!store.isFullscreen}
              trigger={
                <Button hierarchy='tertiary' height='s' icon={<DotsVerticalIcon />} ariaLabel={t('common.options')} />
              }
            >
              <div className={styles['menu-content']}>
                <div className={styles['menu-row']}>
                  <span className={styles['menu-label']}>{t('pinboard.timetablePreview.cellHeight')}</span>
                  <Slider
                    value={[store.timetablePreviewCellMinHeight]}
                    min={14}
                    max={60}
                    step={1}
                    onValueChange={(value) => store.setTimetablePreviewCellMinHeight(value[0])}
                  />
                </div>
              </div>
            </Dropdown>
          </div>
        </div>

        <div className={styles['section-scroll']}>
          <div
            className={styles.grid}
            style={
              {
                gridTemplateColumns: `var(--tt-preview-hour-width) repeat(${store.days.length}, minmax(0, 1fr))`,
                '--tt-cell-min-height': `${store.timetablePreviewCellMinHeight}px`,
              } as CSSProperties
            }
          >
            <div className={styles.corner} />
            {store.days.map((day) => (
              <div key={`day-${day.value}`} className={styles.day}>
                {t(dayToTranslation(day.value))}
              </div>
            ))}

            {hours.map((hour) => (
              <Fragment key={`row-${hour.value}`}>
                <div className={styles['hour-label']} title={hour.label}>
                  {hour.label}
                </div>
                {store.days.map((day) => {
                  const cardUuids = store.cardsBySlotAndResource.get(`${hour.value}:${day.value}:${activeUuid}`);
                  const occupied = Boolean(cardUuids && cardUuids.size > 0);
                  const isPicked = Boolean(cardUuids?.has(cardUuid));
                  // „Grün" = wirklich ablegbar (frei/koexistierbar + Ressourcen verfügbar). Bewusst
                  // strenger als am Board: kein Swap/Tausch in der Vorschau.
                  const valid = interactive && store.isPreviewDroppable(cardUuid, duration, hour.value, day.value);

                  const labels: string[] = [];
                  const segments: Segment[] = [];
                  // Klassenplan: erst alle Teil-Segmente sammeln (mit ihrer vertikalen Board-Position
                  // als Sortierschlüssel), danach gleich breit NEBENEINANDER legen.
                  const classParts: {
                    key: string;
                    sortTop: number;
                    color?: string;
                    textColor?: string;
                    label?: string;
                  }[] = [];
                  cardUuids?.forEach((uuid) => {
                    const card = store.getCardByUuid(uuid);
                    if (!card) return;
                    const subjectColor = card.subject?.timetableConfig?.color ?? undefined;
                    const gridCards = store.gridCards.get(uuid) ?? [];

                    if (kind === 'class') {
                      const relevant = gridCards.filter((cc) => cc.classUuid === activeUuid);
                      const chosen = relevant.length > 0 ? relevant : gridCards.slice(0, 1);
                      if (chosen.length === 0) {
                        classParts.push({
                          key: `${uuid}-full`,
                          sortTop: 0,
                          label: card.subject?.shortName ?? undefined,
                        });
                      }
                      chosen.forEach((cc) => {
                        classParts.push({
                          key: `${uuid}-${cc.uuid}`,
                          sortTop: Number.parseFloat(cc.cssTop ?? '0') || 0,
                          color: colorBySubject ? subjectColor : (cc.cssGradientString ?? undefined),
                          textColor: colorBySubject
                            ? getSubjectCardTextColor(subjectColor)
                            : (cc.textColor ?? undefined),
                          label: card.subject?.shortName ?? undefined,
                        });
                      });
                    } else if (segments.length === 0) {
                      // Lehrkraft/Raum belegt den ganzen Slot: eine deckende Vollfüllung (erstes
                      // Segment MIT Farbe; `[0]` kann bei Teilungen farblos sein → sonst blass).
                      const seg = gridCards.find((cc) => cc.cssGradientString);
                      segments.push({
                        key: uuid,
                        left: '0%',
                        width: '100%',
                        top: '0%',
                        height: '100%',
                        color: colorBySubject ? subjectColor : (seg?.cssGradientString ?? undefined),
                        textColor: colorBySubject
                          ? getSubjectCardTextColor(subjectColor)
                          : (seg?.textColor ?? undefined),
                        label: card.subject?.shortName ?? undefined,
                      });
                    }

                    const classes = card.classes.map((c) => c.shortName).join(', ');
                    const teachers = card.teachers.map((te) => te.shortName).join(', ');
                    const rooms = card.rooms.map((r) => r.name).join(', ');
                    labels.push([card.subject?.shortName, classes, teachers, rooms].filter(Boolean).join(' · '));
                  });

                  if (kind === 'class' && classParts.length > 0) {
                    classParts.sort((a, b) => a.sortTop - b.sortTop);
                    const count = classParts.length;
                    classParts.forEach((part, index) => {
                      segments.push({
                        key: part.key,
                        left: `${(index * 100) / count}%`,
                        width: `${100 / count}%`,
                        top: '0%',
                        height: '100%',
                        color: part.color,
                        textColor: part.textColor,
                        label: part.label,
                      });
                    });
                  }

                  return (
                    <div
                      key={`${day.value}-${hour.value}`}
                      className={classNames(styles.cell, {
                        [styles.occupied]: occupied,
                        [styles['picked-cell']]: isPicked,
                        [styles.valid]: valid,
                        [styles.invalid]: interactive && !valid,
                        [styles.readonly]: !interactive,
                      })}
                      onClick={valid ? () => handleCellClick(day.value, hour) : undefined}
                      // Nativer Tooltip: eine CSS-`::after`-Blase würde von den overflow-Containern
                      // (Scroll-Bereich/Panel) abgeschnitten.
                      title={occupied ? labels.join(' | ') : undefined}
                    >
                      {segments.map((seg) => (
                        <span
                          key={seg.key}
                          className={styles.segment}
                          style={{
                            left: seg.left,
                            width: seg.width,
                            top: seg.top,
                            height: seg.height,
                            background: seg.color,
                          }}
                        >
                          {seg.label && (
                            <span
                              className={styles['segment-label']}
                              style={seg.textColor ? { color: seg.textColor } : undefined}
                            >
                              {seg.label}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

ResourceTimetable.displayName = 'ResourceTimetable';
